import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Breadcrumb from '../components/Breadcrumb';
import { Calendar, Plus, FileText, CheckCircle, AlertTriangle, Download, Eye } from 'lucide-react';

const WeeklyPlans = () => {
  const { currentOrg, hasAIPermission } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [generatingPlan, setGeneratingPlan] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = 'Haftalık Planlar | İlkadım360 Hub';
  }, []);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/weekly-plans`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      }
    } catch (error) {
      console.error('Planlar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async (formData) => {
    setGeneratingPlan(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/weekly-plan/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        const newPlan = await response.json();
        setPlans([newPlan, ...plans]);
        setShowCreateModal(false);
        alert('Haftalık plan başarıyla oluşturuldu! 🎉');
      } else {
        const error = await response.json();
        // Handle error detail - could be string or object
        let errorMessage = 'Bilinmeyen bir hata oluştu';
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (error.detail && typeof error.detail === 'object') {
          errorMessage = error.detail.message || JSON.stringify(error.detail);
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Check for common AI service errors
        if (response.status === 502 || response.status === 503 || response.status === 504) {
          errorMessage = 'AI servisinde geçici bir sorun var. Lütfen birkaç dakika sonra tekrar deneyin.';
        } else if (response.status === 500 && errorMessage.includes('timeout')) {
          errorMessage = 'AI model yanıt vermedi. Lütfen farklı bir model seçin veya daha kısa bir plan deneyin.';
        }
        
        alert(`Hata: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Plan oluşturma hatası:', error);
      // Network or other errors
      let errorMessage = 'Plan oluşturulurken bir hata oluştu.';
      if (error.message && error.message.includes('fetch')) {
        errorMessage = 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
      } else if (error.message && error.message.includes('timeout')) {
        errorMessage = 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.';
      }
      alert(errorMessage);
    } finally {
      setGeneratingPlan(false);
    }
  };

  const handleGeneratePDF = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/weekly-plan/${planId}/pdf`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        alert('PDF başarıyla oluşturuldu! 📄');
        loadPlans(); // Refresh to show PDF URL
      } else {
        const error = await response.json();
        let errorMessage = 'PDF oluşturulurken bir hata oluştu';
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (response.status === 502 || response.status === 503) {
          errorMessage = 'Servis geçici olarak kullanılamıyor. Lütfen tekrar deneyin.';
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('PDF oluşturma hatası:', error);
      alert('PDF oluşturulurken bir bağlantı hatası oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleQualityCheck = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/weekly-plan/${planId}/qa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const qaResult = await response.json();
        alert(`Kalite Kontrolü Tamamlandı!\n\nSkor: ${qaResult.score}/100\nDurum: ${qaResult.passed ? 'Geçti ✅' : 'Başarısız ❌'}`);
        loadPlans(); // Refresh to show QA result
      } else {
        const error = await response.json();
        let errorMessage = 'Kalite kontrolü yapılırken bir hata oluştu';
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (response.status === 502 || response.status === 503) {
          errorMessage = 'AI servisinde geçici bir sorun var. Lütfen birkaç dakika sonra tekrar deneyin.';
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Kalite kontrolü hatası:', error);
      alert('Kalite kontrolü yapılırken bir bağlantı hatası oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handlePreview = async (planId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/weekly-plan/${planId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const plan = await response.json();
        setSelectedPlan(plan);
        setShowPreviewModal(true);
      }
    } catch (error) {
      console.error('Plan yükleme hatası:', error);
    }
  };

  if (!hasAIPermission()) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-12 w-12 text-yellow-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Erişim Yetkiniz Yok</h2>
        <p className="text-gray-600">
          Haftalık plan oluşturma özelliği sadece YÖMEV ekibi ve süper adminler için kullanılabilir.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb 
        items={[
          { label: 'Program & Planlama', path: '/planning' },
          { label: 'Haftalık Planlar (AI)', path: null }
        ]} 
      />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🤖 AI Haftalık Plan Sistemi</h1>
          <p className="text-gray-600">
            Gelişmiş AI ile profesyonel haftalık eğitim planları oluşturun
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 shadow-lg"
        >
          <Plus size={20} />
          Yeni Haftalık Plan Oluştur
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Plan</p>
              <p className="text-2xl font-bold text-gray-900">{plans.length}</p>
            </div>
            <FileText className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">PDF Oluşturulmuş</p>
              <p className="text-2xl font-bold text-gray-900">
                {plans.filter(p => p.pdfUrl).length}
              </p>
            </div>
            <Download className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">QA Geçti</p>
              <p className="text-2xl font-bold text-gray-900">
                {plans.filter(p => p.qaResult?.passed).length}
              </p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ortalama Skor</p>
              <p className="text-2xl font-bold text-gray-900">
                {plans.filter(p => p.qaResult).length > 0
                  ? Math.round(
                      plans
                        .filter(p => p.qaResult)
                        .reduce((sum, p) => sum + p.qaResult.score, 0) /
                        plans.filter(p => p.qaResult).length
                    )
                  : '-'}
              </p>
            </div>
            <Calendar className="text-purple-500" size={32} />
          </div>
        </div>
      </div>

      {/* Plans List */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Haftalık Planlar</h2>
        </div>
        
        {plans.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Henüz haftalık plan oluşturulmamış
            </h3>
            <p className="text-gray-500 mb-6">
              Başlamak için yukarıdaki "Yeni Haftalık Plan Oluştur" butonuna tıklayın
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {plans.map((plan) => (
              <div key={plan.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {plan.theme}
                      </h3>
                      {plan.qaResult && (
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          plan.qaResult.passed 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {plan.qaResult.passed ? '✅ QA Geçti' : '❌ QA Başarısız'}
                        </span>
                      )}
                      {plan.pdfUrl && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800">
                          📄 PDF Hazır
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                      <span className="flex items-center gap-1">
                        <Calendar size={16} />
                        Yıl {plan.year}, Hafta {plan.week}
                      </span>
                      <span>👶 {plan.ageGroup}</span>
                      <span>📅 {plan.duration} Gün</span>
                      {plan.focusAreas.length > 0 && (
                        <span>🎯 {plan.focusAreas.join(', ')}</span>
                      )}
                    </div>
                    
                    {plan.qaResult && (
                      <div className="mt-2">
                        <p className="text-sm text-gray-700">
                          <strong>Kalite Skoru:</strong> {plan.qaResult.score}/100
                        </p>
                        {plan.qaResult.suggestions.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {plan.qaResult.suggestions.length} iyileştirme önerisi
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handlePreview(plan.id)}
                      className="px-3 py-2 text-blue-600 border border-blue-600 rounded hover:bg-blue-50 text-sm"
                      title="Önizleme"
                    >
                      <Eye size={16} />
                    </button>
                    
                    {!plan.pdfUrl && (
                      <button
                        onClick={() => handleGeneratePDF(plan.id)}
                        className="px-3 py-2 text-green-600 border border-green-600 rounded hover:bg-green-50 text-sm"
                        title="PDF Oluştur"
                      >
                        <Download size={16} />
                      </button>
                    )}
                    
                    {!plan.qaResult && (
                      <button
                        onClick={() => handleQualityCheck(plan.id)}
                        className="px-3 py-2 text-purple-600 border border-purple-600 rounded hover:bg-purple-50 text-sm"
                        title="Kalite Kontrolü"
                      >
                        <CheckCircle size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateWeeklyPlanModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreatePlan}
          isGenerating={generatingPlan}
        />
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedPlan && (
        <PreviewModal
          plan={selectedPlan}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedPlan(null);
          }}
        />
      )}
    </div>
  );
};

// Create Weekly Plan Modal Component
const CreateWeeklyPlanModal = ({ onClose, onCreate, isGenerating }) => {
  const [formData, setFormData] = useState({
    year: 1,
    week: 1,
    theme: '',
    ageGroup: '3-4 yaş',
    duration: 5,
    focusAreas: [],
    additionalNotes: '',
    aiModel: 'claude-sonnet'  // Varsayılan: Claude (daha stabil)
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.theme.trim()) {
      alert('Lütfen bir tema girin');
      return;
    }
    onCreate(formData);
  };

  const toggleFocusArea = (area) => {
    setFormData(prev => ({
      ...prev,
      focusAreas: prev.focusAreas.includes(area)
        ? prev.focusAreas.filter(a => a !== area)
        : [...prev.focusAreas, area]
    }));
  };

  const focusAreaOptions = [
    'Dil ve İletişim Gelişimi',
    'Bilişsel Gelişim',
    'Sosyal-Duygusal Gelişim',
    'Fiziksel Gelişim',
    'Matematik',
    'Sanat',
    'Müzik',
    'Fen ve Doğa'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">
            🤖 Yeni Haftalık Plan Oluştur
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            AI ile profesyonel haftalık eğitim planı oluşturun
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Yılı
              </label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value={1}>Yıl 1 (0-2 yaş)</option>
                <option value={2}>Yıl 2 (2-3 yaş)</option>
                <option value={3}>Yıl 3 (3-6 yaş)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hafta Numarası
              </label>
              <input
                type="number"
                min="1"
                max="40"
                value={formData.week}
                onChange={(e) => setFormData({...formData, week: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Haftalık Tema *
            </label>
            <input
              type="text"
              value={formData.theme}
              onChange={(e) => setFormData({...formData, theme: e.target.value})}
              placeholder="Örn: Mevsimler ve Doğa"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yaş Grubu
              </label>
              <select
                value={formData.ageGroup}
                onChange={(e) => setFormData({...formData, ageGroup: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="0-2 yaş">0-2 yaş (Bebekler)</option>
                <option value="2-3 yaş">2-3 yaş (Toddler)</option>
                <option value="3-4 yaş">3-4 yaş</option>
                <option value="4-5 yaş">4-5 yaş</option>
                <option value="5-6 yaş">5-6 yaş</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Süresi (Gün)
              </label>
              <input
                type="number"
                min="1"
                max="5"
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Odak Alanları (Opsiyonel)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {focusAreaOptions.map(area => (
                <label key={area} className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.focusAreas.includes(area)}
                    onChange={() => toggleFocusArea(area)}
                    className="rounded text-blue-600"
                  />
                  <span className="text-sm">{area}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              AI Model Seçimi
            </label>
            <select
              value={formData.aiModel}
              onChange={(e) => setFormData({...formData, aiModel: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="claude-sonnet">Claude Sonnet 4 (Önerilen - Dengeli)</option>
              <option value="openai-gpt5">OpenAI GPT-5.1 (En Güçlü)</option>
              <option value="gemini-flash">Gemini 2.5 Flash (En Hızlı)</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Claude Sonnet dengeli ve güvenilir sonuç verir
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ek Notlar (Opsiyonel)
            </label>
            <textarea
              value={formData.additionalNotes}
              onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})}
              rows={3}
              placeholder="AI'ya özel talimatlar veya notlar ekleyebilirsiniz..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Bildirim Mesajı */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              ℹ️ <strong>Plan oluşturma 1-2 dakika sürebilir.</strong> Lütfen sayfayı kapatmayın.
            </p>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              disabled={isGenerating}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={isGenerating}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Plan Oluşturuluyor...
                </span>
              ) : (
                '🤖 AI ile Oluştur'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Preview Modal Component
const PreviewModal = ({ plan, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">{plan.theme}</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Yıl {plan.year} • Hafta {plan.week} • {plan.ageGroup}
          </p>
        </div>
        
        <div className="p-6">
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ 
              __html: plan.markdownContent
                .replace(/^# /gm, '<h1 class="text-2xl font-bold text-gray-900 mt-6 mb-3">')
                .replace(/^## /gm, '<h2 class="text-xl font-bold text-gray-800 mt-5 mb-2 border-l-4 border-blue-500 pl-3">')
                .replace(/^### /gm, '<h3 class="text-lg font-semibold text-gray-700 mt-4 mb-2">')
                .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
                .replace(/^- /gm, '<li class="ml-4">')
                .replace(/\n\n/g, '</p><p class="mb-3">')
            }}
          />
        </div>
        
        <div className="p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyPlans;
